// src/components/forms/variants/v2/popup/PopupEmailCapture.tsx

'use client'

import { forwardRef } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import { cn } from '~/lib/utils'
import { BaseEmailCaptureForm } from '~/components/forms/base/BaseEmailCaptureForm'
import { FormErrorBoundary } from '~/components/forms/base/ErrorBoundary'
import type { RenderProps } from '~/components/forms/base/types'
import type { PopupEmailCaptureProps } from '~/components/forms/variants/popup/types'
import { POPUP_PRESET } from '~/components/forms/variants/popup/types'
import Image from 'next/image'
import { siteConfig } from '~/lib/config/site'

export const PopupEmailCapture = forwardRef<HTMLFormElement, PopupEmailCaptureProps>(({
  isOpen,
  onClose,
  theme: customTheme,
  content,
  className,
  ...props
}, ref) => {
  const theme = {
    ...POPUP_PRESET.theme,
    ...customTheme,
    className: {
      ...POPUP_PRESET.theme?.className,
      ...customTheme?.className
    }
  }

  const renderForm = ({ state, handlers }: RenderProps): JSX.Element => {
    const { email, error, isSubmitting, isSuccess } = state
    const { handleChange, handleSubmit, handleBlur } = handlers

    return (
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100]">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cn(
                "fixed inset-0",
                theme.backdrop?.className ?? "bg-gray-900/50 backdrop-blur-sm dark:bg-gray-900/70"
              )}
              style={theme.backdrop?.style}
              onClick={onClose}
            />

            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed inset-4 flex items-center justify-center sm:inset-0"
            >
              <div className={cn(
                "relative w-full max-w-xl overflow-hidden rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 shadow-2xl ring-1 ring-gray-200/50 dark:ring-gray-800/50",
                theme.className?.container,
                theme.className?.form,
                className
              )}>
                {/* Close Button */}
                <motion.button
                  onClick={onClose}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="absolute right-4 top-4 z-10 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 
                           dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-300
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                >
                  <RiCloseLine className="h-5 w-5" />
                </motion.button>

                <div className="grid gap-6 sm:grid-cols-2">
                  {/* Image Section */}
                  {content.image && (
                    <div className="relative hidden sm:block">
                      <div className="absolute inset-0">
                        <motion.div 
                          className="h-full w-full"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.4 }}
                        >
                          <Image
                            src={content.image.src}
                            alt={content.image.alt ?? ''}
                            fill
                            className="object-cover"
                            sizes="50vw"
                          />
                          <div 
                            className="absolute inset-0 bg-gradient-to-br from-indigo-600/80 to-purple-600/80 mix-blend-multiply backdrop-blur-[2px]"
                          />
                        </motion.div>
                      </div>
                    </div>
                  )}

                  {/* Form Section */}
                  <div className="p-6 sm:p-8">
                    <div className="space-y-6">
                      {/* Title */}
                      {content.title && (
                        <motion.h2
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={cn(
                            "text-2xl font-bold tracking-tight text-gray-900 dark:text-white",
                            content.title.className
                          )}
                        >
                          {content.title.text}
                        </motion.h2>
                      )}

                      {/* Description */}
                      {content.description && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                          className={cn(
                            "text-base text-gray-600 dark:text-gray-300",
                            content.description.className
                          )}
                        >
                          {content.description.text}
                        </motion.p>
                      )}

                      {/* Form */}
                      <motion.form
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        onSubmit={async (e) => {
                          e.preventDefault()
                          await handleSubmit(e)
                        }}
                        className="space-y-4"
                      >
                        <div className="relative">
                          <input
                            type="email"
                            value={email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={content.input?.placeholder}
                            className={cn(
                              "block w-full rounded-xl border-0 px-4 py-4",
                              "text-gray-900 dark:text-white",
                              "bg-gray-50/50 dark:bg-gray-800/30",
                              "shadow-sm ring-1 ring-inset",
                              "ring-gray-200 dark:ring-gray-700/50",
                              "placeholder:text-gray-500 dark:placeholder:text-gray-400",
                              "focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:focus:ring-indigo-400",
                              "hover:ring-gray-300 dark:hover:ring-gray-600",
                              "transition-shadow duration-200",
                              error && "ring-red-300 dark:ring-red-500/30 focus:ring-red-500",
                              isSuccess && "ring-green-300 dark:ring-green-500/30",
                              content.input?.className
                            )}
                            disabled={isSubmitting || isSuccess}
                          />
                          {error && (
                            <motion.p
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400"
                            >
                              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                                <span className="h-2 w-2 rounded-full bg-red-600 dark:bg-red-400" />
                              </span>
                              {error}
                            </motion.p>
                          )}
                        </div>

                        <button
                          type="submit"
                          disabled={isSubmitting || isSuccess}
                          aria-label={isSubmitting ? content.button?.loadingText : content.button?.text}
                          onClick={async (e) => {
                            e.preventDefault()
                            await handleSubmit(e)
                          }}
                          className={cn(
                            "flex w-full items-center justify-center gap-2 rounded-xl",
                            "bg-gradient-to-r from-indigo-600 to-indigo-500",
                            "px-4 py-4 text-base font-semibold text-white shadow-sm",
                            "hover:from-indigo-500 hover:to-indigo-400",
                            "focus:outline-none focus:ring-2",
                            "focus:ring-indigo-500 dark:focus:ring-indigo-400",
                            "focus:ring-offset-2 dark:focus:ring-offset-gray-900",
                            "disabled:opacity-50 dark:disabled:opacity-40",
                            "transition-all duration-200",
                            "transform hover:scale-[1.02] active:scale-[0.98]",
                            isSuccess && "bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400",
                            content.button?.className
                          )}
                        >
                          {isSuccess ? (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="flex items-center gap-2"
                            >
                              <svg
                                className="h-5 w-5 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <motion.path
                                  initial={{ pathLength: 0 }}
                                  animate={{ pathLength: 1 }}
                                  transition={{ duration: 0.5 }}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              <span>{content.messages?.success}</span>
                            </motion.div>
                          ) : (
                            <>
                              <span>{isSubmitting ? content.button?.loadingText : content.button?.text}</span>
                              {content.button?.icon}
                            </>
                          )}
                        </button>

                        {/* Privacy Notice */}
                        {content.privacy?.enabled && (
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className={cn(
                              "text-xs text-gray-500 dark:text-gray-400",
                              content.privacy.className
                            )}
                          >
                            {content.privacy.text}{' '}
                            {content.privacy.privacyLink?.url && (
                              <a
                                href={content.privacy.privacyLink?.url ?? `${siteConfig.baseLinks.privacy}`}
                                className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                              >
                                {content.privacy.privacyLink?.text ?? 'Privacy Policy'}
                              </a>
                            )}
                            {content.privacy.termsLink?.url && (
                              <> and{' '}
                                <a
                                  href={content.privacy.termsLink?.url ?? `${siteConfig.baseLinks.terms}`}
                                  className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                                >
                                  {content.privacy.termsLink?.text ?? 'Terms of Service'}
                                </a>
                              </>
                            )}
                          </motion.p>
                        )}
                      </motion.form>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    )
  }

  return (
    <FormErrorBoundary>
      <BaseEmailCaptureForm
        ref={ref}
        theme={theme}
        content={content}
        {...props}
      >
        {renderForm}
      </BaseEmailCaptureForm>
    </FormErrorBoundary>
  )
})

PopupEmailCapture.displayName = 'PopupEmailCapture' 